import React, { useCallback, useEffect } from 'react'
import { Card, Image, ListGroup, Spinner } from 'react-bootstrap'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetMeQuery } from '../../store/user/user'
import { Button } from '../../components/Button/Button'
import { useNavigate, useSearchParams } from 'react-router-dom'
import './Authorize.scss'
import { OAuth2App, useLazyGetOAuth2AppsQuery, useOauth2AuthorizeMutation } from '../../store/auth/auth'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { isApiErrors } from '../../models/ApiErrors'

interface OAuth2Error {
  error: string
  error_description: string
}

export const OAuthAuthorize: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const [searchParams] = useSearchParams()
  const [initialLoading, setInitialLoading] = React.useState(true)
  const [errors, setErrors] = React.useState<string[]>([])
  const [clientId, setClientId] = React.useState<string | null>(null)
  const [redirectUri, setRedirectUri] = React.useState<string | null>(null)
  const [scopes, setScopes] = React.useState<string[] | null>(null)
  const [state, setState] = React.useState<string | null>(null)
  const [responseType, setResponseType] = React.useState<string | null>(null)
  const [oauth2App, setOAuth2App] = React.useState<OAuth2App | null>(null)
  const [triggerGetOAuth2App, { isFetching: isFetchingOAuth2App }] = useLazyGetOAuth2AppsQuery()

  const validateParams = (client_id: string | null, redirect_uri: string | null, scopes: string | null, responseType: string | null) => {
    const newErrors = []
    if (!client_id) {
      newErrors.push('client_id is required')
    }
    if (!redirect_uri) {
      newErrors.push('redirect_uri is required')
    }
    if (!scopes) {
      newErrors.push('scope is required')
    }
    if (!responseType) {
      newErrors.push('response_type is required')
    }
    setErrors(newErrors)
    return newErrors.length === 0
  }

  const initOAuth2App = useCallback(
    async (accountId: string, clientId: string) => {
      const response = await triggerGetOAuth2App({ accountId, clientId })

      if (response.isError || !response.data?.data) {
        const fetchError = 'error' in response && response.error && 'status' in response.error ? (response.error as FetchBaseQueryError) : undefined

        const status = fetchError?.status as number | undefined
        const errorBody = fetchError?.data

        if (status === 404) {
          setErrors([`OAuth2 Application with client_id=${clientId} was not found!`])
        } else if (status === 400 && isApiErrors(errorBody) && errorBody.errors.length) {
          setErrors(['OAuth2 integration is not enabled for this account.'])
        } else {
          setErrors(['An unexpected error occurred while fetching the OAuth2 application.'])
        }

        setInitialLoading(false)
        return
      }

      setOAuth2App(response.data.data)
      setInitialLoading(false)
    },
    [triggerGetOAuth2App],
  )

  const handleError = (error: OAuth2Error) => {
    setErrors([`${error.error}: ${error.error_description}`])
  }

  useEffect(() => {
    const clientId = searchParams.get('client_id')
    const redirectUri = searchParams.get('redirect_uri')
    const scopes = searchParams.get('scope')
    const state = searchParams.get('state')
    const responseType = searchParams.get('response_type')
    const isValidParams = validateParams(clientId, redirectUri, scopes, responseType)
    if (isValidParams) {
      setClientId(clientId)
      setRedirectUri(redirectUri)
      setScopes(scopes!.split(' '))
      setState(state)
      setResponseType(responseType)
      initOAuth2App(accountId, clientId!)
    } else {
      setInitialLoading(false)
    }
  }, [searchParams, initOAuth2App, accountId])

  return (
    <div className='d-flex align-items-center justify-content-center vh-100 bg-light p-3'>
      <Card className='shadow-lg' style={{ width: '22rem', borderRadius: '1rem' }}>
        <Card.Body className='text-center'>
          {initialLoading || isFetchingOAuth2App ? (
            <Spinner />
          ) : errors.length || !oauth2App || !clientId || !redirectUri || !scopes || !responseType ? (
            <OAuthAuthorizeErrors errors={errors} />
          ) : (
            <OAuthAuthorizeDefault
              clientId={clientId}
              redirectUri={redirectUri}
              scopes={scopes}
              state={state}
              oauth2App={oauth2App}
              responseType={responseType}
              onError={handleError}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

interface OAuthAuthorizeErrors {
  errors: string[]
}

const OAuthAuthorizeErrors: React.FC<OAuthAuthorizeErrors> = ({ errors }) => {
  return (
    <>
      <Card.Text className='mb-2 text-secondary fw-semibold'>Invalid request:</Card.Text>

      <ListGroup variant='flush' className='mb-3 text-start small'>
        {errors.map((error) => (
          <ListGroup.Item key={error} className='bg-light border-0 ps-3'>
            • {error}
          </ListGroup.Item>
        ))}
      </ListGroup>
    </>
  )
}

interface OAuthAuthorizeDefaultProps {
  clientId: string
  redirectUri: string
  scopes: string[]
  state: string | null
  responseType: string
  oauth2App: OAuth2App
  onError: (error: OAuth2Error) => void
}

const OAuthAuthorizeDefault: React.FC<OAuthAuthorizeDefaultProps> = ({ clientId, redirectUri, scopes, state, responseType, oauth2App, onError }) => {
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser, isFetching: isFetchingMe } = useGetMeQuery({ accountId })
  const [triggerOAuth2Authorize, { isLoading: isLoadingAuthorize, isSuccess: isSuccessAuthorize }] = useOauth2AuthorizeMutation()

  const handleAuthorize = async () => {
    const response = await triggerOAuth2Authorize({
      accountId,
      body: {
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: responseType,
        scope: scopes.join(' '),
        state,
      },
    })

    if (response.error && 'data' in response.error) {
      onError(response.error.data as OAuth2Error)
      return
    }
    if (response.data) {
      window.location.href = response.data.data.redirectUri
    }
  }

  return (
    <>
      {isFetchingMe || isSuccessAuthorize ? (
        <Spinner />
      ) : (
        <>
          {oauth2App.imageUrl ? (
            <Image src={oauth2App.imageUrl} alt={`${oauth2App.name} logo`} roundedCircle className='mb-3 app-logo' />
          ) : (
            <>
              <i className='bi bi-shield-lock text-primary mb-3' />
            </>
          )}

          <Card.Title className='mb-2'>
            <strong>{oauth2App.name}</strong> is requesting access
          </Card.Title>

          {currentUser && (
            <div className='text-muted mb-3 d-flex align-items-center justify-content-center gap-2'>
              <i className='bi bi-person-circle' />
              <small>
                Signed in as <strong>{currentUser.name}</strong>
              </small>
            </div>
          )}

          <Card.Text className='mb-2 text-secondary fw-semibold'>This app will be able to:</Card.Text>

          <ListGroup variant='flush' className='mb-3 text-start small'>
            {scopes?.map((scope) => (
              <ListGroup.Item key={scope} className='bg-light border-0 ps-3'>
                • {scope}
              </ListGroup.Item>
            ))}
          </ListGroup>

          <div className='d-flex justify-content-between mt-3'>
            <Button
              variant='secondary'
              onClick={() => {
                navigate('/')
              }}
              disabled={false}
              className='w-50 me-2'
            >
              Deny
            </Button>

            <Button variant='primary' onClick={handleAuthorize} disabled={false} className='w-50 ms-2' loading={isLoadingAuthorize}>
              Authorize
            </Button>
          </div>

          <div className='text-muted mt-3 small' style={{ lineHeight: '1.5' }}>
            By clicking Authorize, you allow this app and Zocks to use your information in accordance with their respective Terms of Service and Privacy Policy.
          </div>
        </>
      )}
    </>
  )
}
